import React, { useCallback, useContext, useState } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import { Icon, Link, ModalNextProvider,FormattedDate } from '@jutro/components';
import { renderContentFromMetadata } from '@jutro/uiconfig';
import { DocumentService } from 'gw-capability-policydocument';
import { IntlContext } from '@jutro/locale';
import { useAuthentication, withAuthenticationContext } from 'gw-digital-auth-react';
import { PolicyService } from 'gw-capability-policy';
import { messages as commonMessages } from 'gw-platform-translations';
import _ from 'lodash';
import metadata from './AccountDocumentSummary.metadata.json5';
import styles from './AccountDocumentSummary.module.scss';
import messages from '../PolicyCommonDocuments.messages.js'

const getDownloadButton = (document) => {
    const link = DocumentService.downloadPolicyDocument(document.publicID, document.sessionID);
    const label = `${"download" + ' '}${ document.name}, Opens a new tab`;
    return (
        <Link aria-label = {label} href={link} target="_blank">
            <Icon tabIndex = "-1" icon="fas fa-cloud-download-alt" className={styles.iconSize,styles.deleteBtn} />
        </Link>
    );
};

function AccountDocumentSummary(props) {
    const {
        value: { documentDTOs: policyDocuments, policyNumber: passedPolicyNumber },
        showFileUpload, layout
    } = props;
    const { authHeader } = useAuthentication();
    const intl = useContext(IntlContext);
    const [policyDocumentsState, updatePolicyDocumentsState] = useState(policyDocuments);

    const claimsDocUploadToken = useCallback(async () => {
        try {
            const uploadTokenID = await PolicyService.getDocumentUploadToken([], authHeader);
            return uploadTokenID;
        } catch (e) {
            return ModalNextProvider.showAlert({
                title: commonMessages.errorUploadTitle,
                message: commonMessages.errorGenerateUploadToken,
                status: 'error',
                icon: 'mi-error-outline',
                confirmButtonText: commonMessages.ok
            }).catch(_.noop);
        }
    }, [authHeader]);

    const uploadDocument = useCallback(
        async (item) => {
            const documentMetaDataTemplate = {
                docUID: '001',
                documentType: 'letter_received',
                securityType: 'unrestricted',
                status: 'approved',
                policyNumber: passedPolicyNumber,
                name: item.name,
                mimeType: item.type,
                SessionID: await claimsDocUploadToken()
            };
            try {
                const recentUploadedDoc = await PolicyService.uploadDocument(
                    item,
                    documentMetaDataTemplate,
                    authHeader
                );
                if (!_.isEmpty(recentUploadedDoc)) {
                    updatePolicyDocumentsState((docs) => [...docs, recentUploadedDoc]);
                }
            } catch (error) {
                ModalNextProvider.showAlert({
                    title: messages.documentUploadFailed,
                    message: messages.documentCannotUploaded,
                    status: 'error',
                    icon: 'mi-error-outline',
                    confirmButtonText: commonMessages.ok
                }).catch(_.noop);
            }
        },
        [authHeader, claimsDocUploadToken, passedPolicyNumber, policyDocumentsState]
    );

    const deleteDocument = useCallback(
        (event, item) => {
            event.preventDefault();
            if (!item.canDelete) {
                return;
            }

            const { publicID } = item;
            ModalNextProvider.showConfirm({
                title: messages.removeDocument,
                message: messages.confirmRemoveDocument,
                status: 'warning',
                icon: 'mi-error-outline',
                confirmButtonText: messages.documentRemoveConfirmYes,
                cancelButtonText: messages.documentRemoveConfirmNo
            }).then(async (results) => {
                if (results === 'cancel') {
                    return _.noop();
                }
                try {
                    const isDocumentDeleted = await PolicyService.removeDocument(
                        publicID,
                        authHeader
                    );
                    if (isDocumentDeleted) {
                        const newDocuments = _.filter(policyDocumentsState, (doc) => {
                            return doc.workingPublicID !== publicID;
                        });
                        updatePolicyDocumentsState(newDocuments);
                    }
                } catch (documentDeletionError) {
                    ModalNextProvider.showAlert({
                        title: messages.documentDeletionFailed,
                        message: messages.documentCannotDeleted,
                        status: 'error',
                        icon: 'mi-error-outline',
                        confirmButtonText: commonMessages.ok
                    }).catch(_.noop);
                }
                return true;
            }, _.noop);
        },
        [authHeader, policyDocumentsState]
    );

    const getDeleteButton = (document) => {
        if (document.canDelete) {
            return (
                <Icon aria-label="delete" onClick={(e) => deleteDocument(e, document)} icon="fas fa-trash-alt" className={styles.iconSize,styles.deleteBtn} />
            );
        }
        return null;
    };

    const getDocumentName = (document) => {
        return (
            <span aria-label={document.name} className={styles.documentName}>{ document.name }</span>
        );
    };

    const getFormattedDate = useCallback(
        (date) => {
          return <FormattedDate value={new Date(date)} format="short" />;
        }
      );

    const getDateUploaded = (document) => {
        // eslint-disable-next-line max-len
        return <span className={styles.dateUploaded}>{getFormattedDate(document.dateModified)}</span>;
    };

    // const getFileName = useCallback(
    //     (document) => {
    // eslint-disable-next-line max-len
    //         const link = DocumentService.downloadPolicyDocument(document.publicID, document.sessionID); alert(link);
    //         window.open({ link }, '_blank');
    //     },
    //     [policyDocuments]
    // );

    const generatePolicyCardOverrides = useCallback(() => {
        const overrides = policyDocumentsState.map((documentDTO, index) => {
            return {
                // [`rightSection${index}`]: {
                // eslint-disable-next-line max-len
                //     className: getDeleteButton(documentDTO) === '' ? 'rightSection' : 'rightSectionAlt rightSection'
                // },
                [`documentNameId${index}`]: {
                    content: getDocumentName(documentDTO)
                },
                [`dateUploadedId${index}`]: {
                    content: getDateUploaded(documentDTO)
                },
                [`downloadButton${index}`]: {
                    content: getDownloadButton(documentDTO)
                },
                [`deleteButton${index}`]: {
                    content: getDeleteButton(documentDTO),
                    visible: documentDTO.canDelete ? true : false
                },

            };
        });
        return Object.assign({}, ...overrides);
    }, [policyDocumentsState]);

    const overrideProps = {
        noDataMessageContainer: {
            visible: policyDocumentsState.length === 0
        },
        repeatCard: {
            data: policyDocumentsState
        },
        pageContainer: {
            visible: policyDocumentsState.length > 0
        },
        fileUploadComponent: {
            visible: showFileUpload
        },
        policyCardContainer1: {
            columns: layout
        },
        ...generatePolicyCardOverrides(),
    };

    const resolvers = {
        resolveClassNameMap: styles,
        resolveCallbackMap: {
            onFileUpload: uploadDocument
        }
    };

    return renderContentFromMetadata(metadata.pageContent, overrideProps, resolvers);
}

AccountDocumentSummary.propTypes = {
    value: PropTypes.shape({
        documentDTOs: PropTypes.arrayOf(PropTypes.shape({})),
    }),
    match: PropTypes.shape({
        params: PropTypes.shape({
            streamId: PropTypes.string
        })
    }).isRequired,
    history: PropTypes.shape({
        push: PropTypes.func
    }).isRequired,
    authHeader: PropTypes.shape({}).isRequired,
    getDownloadLink: PropTypes.func.isRequired,
    onDocumentUpload: PropTypes.func.isRequired,
    showFileUpload: PropTypes.shape([]).isRequired,
};

AccountDocumentSummary.defaultProps = {
    value: {
        documentDTOs: [],
    }
};

export default withAuthenticationContext(withRouter(AccountDocumentSummary));
